import * as React from 'react'

import { buildResourceCardModel } from '~/client/src/shared/components/ResourceCardList/resourceCardMapper'
import {
  ICardConfiguration,
  IResourceCardModel,
  IResourceProperty,
  IResourceRecord,
} from '~/client/src/shared/components/ResourceCardList/types'
import { ILinkedItem } from '~/client/src/shared/components/ResourceLink'
import useEquipmentResources from '~/client/src/shared/hooks/useEquipmentResources'
import { useResourceCardSchema } from '~/client/src/shared/hooks/useResourceCardSchema'
import { useResourceLink } from '~/client/src/shared/hooks/useResourceLink'
import { useResourceRuntimeContext } from '~/client/src/shared/hooks/useResourceRuntimeContext'
import useSensors from '~/client/src/shared/hooks/useSensors'
import { renderResourceRecordIcon } from '~/client/src/shared/resources/icons/resourceRecordIcon'
import { renderResourceSemanticIcon } from '~/client/src/shared/resources/icons/resourceSemanticIcons'
import { EQUIPMENT_SENSOR_RESOURCE_LINK } from '~/client/src/shared/resources/resourceLink/managedResourceLinks'

interface ILinkActionResult {
  success: boolean
  error?: string | null
}

interface ILinkRootProps {
  sourceId: string | null
  linkedItem: ILinkedItem | null
  availableTargets: ILinkedItem[]
  onLink: (sourceId: string, targetId: string) => Promise<ILinkActionResult>
  onUnlink: (sourceId: string) => Promise<ILinkActionResult>
}

export interface IResourceLinkFieldResult {
  linkedCardModel: IResourceCardModel | null
  triggerLabel: string
  pickerTitle: string
  emptyMessage: string
  /** Type-level icon: the picker rows' fallback when a record carries no glyph of its own. */
  leadingIcon: React.ReactNode
  /** The linked record's own glyph, for the trigger card. Falls back to leadingIcon. */
  linkedLeadingIcon?: React.ReactNode
  targetRecords: IResourceRecord[]
  targetResourceTypeId: string
  targetCardConfiguration: ICardConfiguration
  targetProperties: IResourceProperty[]
  getTargetDisplayValue: (propertyName: string, value: any) => string
  linkRootProps: ILinkRootProps
}

interface IUseResourceLinkFieldParams {
  projectId?: string
  sourceRecordId: string | null
  sourceRecord: IResourceRecord | null
}

function buildLinkedCardModel({
  linkedItem,
  records,
  resourceTypeId,
  cardConfiguration,
  properties,
  getDisplayValue,
}: {
  linkedItem: ILinkedItem | null
  records: IResourceRecord[]
  resourceTypeId: string
  cardConfiguration: ICardConfiguration
  properties: IResourceProperty[]
  getDisplayValue: (propertyName: string, value: any) => string
}): IResourceCardModel | null {
  if (!linkedItem) return null

  const record = records.find(item => item.id === linkedItem.id)
  if (!record) return null

  return buildResourceCardModel({
    record,
    resourceTypeId,
    cardConfiguration,
    properties,
    getDisplayValue,
  })
}

export function useEquipmentSensorLinkField({
  projectId,
  sourceRecordId,
  sourceRecord,
}: IUseResourceLinkFieldParams): IResourceLinkFieldResult {
  const { records: sensorRecords, schema: sensorSchema } = useSensors({
    projectId,
  })
  const runtimeContext = useResourceRuntimeContext()
  const resourceLinkApi = useResourceLink({
    projectId: projectId || '',
    kindId: EQUIPMENT_SENSOR_RESOURCE_LINK.kindId,
  })
  const sensorCardSchema = useResourceCardSchema({
    resourceTypeId: EQUIPMENT_SENSOR_RESOURCE_LINK.target.resourceTypeId,
    runtimeContext,
    schema: sensorSchema,
  })

  const linkedItem = React.useMemo<ILinkedItem | null>(() => {
    const sensor = sourceRecord?.root?.linkedSensor
    if (!sensor) return null

    return {
      id: String(sensor.id),
      name: sensor.name,
      infuseId: sensor.infuseId ?? null,
    }
  }, [sourceRecord])

  const availableTargets = React.useMemo<ILinkedItem[]>(() => {
    return sensorRecords
      .filter(record => {
        const linked = record.root?.linkedEquipment
        if (!linked) return true
        return String(linked.id) === sourceRecordId
      })
      .map(record => ({
        id: record.id,
        name: record.name,
        infuseId: record.infuseId ?? null,
      }))
  }, [sensorRecords, sourceRecordId])

  const handleLink = React.useCallback(
    async (equipmentId: string, sensorId: string) => {
      if (linkedItem) {
        const unlinkResult = await resourceLinkApi.unlink({
          sourceId: equipmentId,
        })
        if (!unlinkResult.success) return unlinkResult
      }

      return resourceLinkApi.link({
        sourceId: equipmentId,
        targetId: sensorId,
      })
    },
    [linkedItem, resourceLinkApi],
  )

  const handleUnlink = React.useCallback(
    async (equipmentId: string) => {
      return resourceLinkApi.unlink({ sourceId: equipmentId })
    },
    [resourceLinkApi],
  )

  const linkedCardModel = React.useMemo(() => {
    return buildLinkedCardModel({
      linkedItem,
      records: sensorRecords,
      resourceTypeId: EQUIPMENT_SENSOR_RESOURCE_LINK.target.resourceTypeId,
      cardConfiguration: sensorCardSchema.cardConfiguration,
      properties: sensorCardSchema.properties,
      getDisplayValue: sensorCardSchema.getDisplayValue,
    })
  }, [linkedItem, sensorRecords, sensorCardSchema])

  return {
    linkedCardModel,
    triggerLabel: EQUIPMENT_SENSOR_RESOURCE_LINK.target.label,
    pickerTitle: EQUIPMENT_SENSOR_RESOURCE_LINK.target.pickerTitle,
    emptyMessage: EQUIPMENT_SENSOR_RESOURCE_LINK.target.emptyMessage,
    leadingIcon: renderResourceSemanticIcon(
      EQUIPMENT_SENSOR_RESOURCE_LINK.target.semanticIconKey,
      'tw:block tw:size-full tw:text-muted-foreground',
    ),
    targetRecords: sensorRecords,
    targetResourceTypeId: EQUIPMENT_SENSOR_RESOURCE_LINK.target.resourceTypeId,
    targetCardConfiguration: sensorCardSchema.cardConfiguration,
    targetProperties: sensorCardSchema.properties,
    getTargetDisplayValue: sensorCardSchema.getDisplayValue,
    linkRootProps: {
      sourceId: sourceRecordId,
      linkedItem,
      availableTargets,
      onLink: handleLink,
      onUnlink: handleUnlink,
    },
  }
}

export function useSensorEquipmentLinkField({
  projectId,
  sourceRecordId,
  sourceRecord,
}: IUseResourceLinkFieldParams): IResourceLinkFieldResult {
  const { records: equipmentRecords, schema: equipmentSchema } =
    useEquipmentResources({ projectId })
  const runtimeContext = useResourceRuntimeContext()
  const resourceLinkApi = useResourceLink({
    projectId: projectId || '',
    kindId: EQUIPMENT_SENSOR_RESOURCE_LINK.kindId,
  })
  const equipmentCardSchema = useResourceCardSchema({
    resourceTypeId: EQUIPMENT_SENSOR_RESOURCE_LINK.source.resourceTypeId,
    runtimeContext,
    schema: equipmentSchema,
  })

  const linkedItem = React.useMemo<ILinkedItem | null>(() => {
    const equipment = sourceRecord?.root?.linkedEquipment
    if (!equipment) return null

    return {
      id: String(equipment.id),
      name: equipment.name,
    }
  }, [sourceRecord])

  const availableTargets = React.useMemo<ILinkedItem[]>(() => {
    return equipmentRecords
      .filter(record => {
        const linked = record.root?.linkedSensor
        if (!linked) return true
        return String(linked.id) === sourceRecordId
      })
      .map(record => ({
        id: record.id,
        name: record.name,
      }))
  }, [equipmentRecords, sourceRecordId])

  const handleLink = React.useCallback(
    async (sensorId: string, equipmentId: string) => {
      if (linkedItem) {
        const unlinkResult = await resourceLinkApi.unlink({
          sourceId: linkedItem.id,
        })
        if (!unlinkResult.success) return unlinkResult
      }

      return resourceLinkApi.link({
        sourceId: equipmentId,
        targetId: sensorId,
      })
    },
    [linkedItem, resourceLinkApi],
  )

  const handleUnlink = React.useCallback(async () => {
    if (!linkedItem) {
      return { success: false, error: 'No linked equipment' }
    }

    return resourceLinkApi.unlink({ sourceId: linkedItem.id })
  }, [linkedItem, resourceLinkApi])

  const linkedCardModel = React.useMemo(() => {
    return buildLinkedCardModel({
      linkedItem,
      records: equipmentRecords,
      resourceTypeId: EQUIPMENT_SENSOR_RESOURCE_LINK.source.resourceTypeId,
      cardConfiguration: equipmentCardSchema.cardConfiguration,
      properties: equipmentCardSchema.properties,
      getDisplayValue: equipmentCardSchema.getDisplayValue,
    })
  }, [linkedItem, equipmentRecords, equipmentCardSchema])

  return {
    linkedCardModel,
    triggerLabel: EQUIPMENT_SENSOR_RESOURCE_LINK.source.label,
    pickerTitle: EQUIPMENT_SENSOR_RESOURCE_LINK.source.pickerTitle,
    emptyMessage: EQUIPMENT_SENSOR_RESOURCE_LINK.source.emptyMessage,
    leadingIcon: renderResourceSemanticIcon(
      EQUIPMENT_SENSOR_RESOURCE_LINK.source.semanticIconKey,
      'tw:block tw:size-full tw:text-muted-foreground',
    ),
    // The trigger card shows the one linked equipment, so it gets that record's own glyph.
    linkedLeadingIcon: renderResourceRecordIcon(
      EQUIPMENT_SENSOR_RESOURCE_LINK.source.resourceTypeId,
      equipmentRecords.find(r => r.id === linkedItem?.id),
      renderResourceSemanticIcon(
        EQUIPMENT_SENSOR_RESOURCE_LINK.source.semanticIconKey,
        'tw:block tw:size-full tw:text-muted-foreground',
      ),
      'tw:block tw:size-full tw:text-muted-foreground',
    ),
    targetRecords: equipmentRecords,
    targetResourceTypeId: EQUIPMENT_SENSOR_RESOURCE_LINK.source.resourceTypeId,
    targetCardConfiguration: equipmentCardSchema.cardConfiguration,
    targetProperties: equipmentCardSchema.properties,
    getTargetDisplayValue: equipmentCardSchema.getDisplayValue,
    linkRootProps: {
      sourceId: sourceRecordId,
      linkedItem,
      availableTargets,
      onLink: handleLink,
      onUnlink: handleUnlink,
    },
  }
}
